import React, { useState, useRef, useEffect } from "react";
import { FaExternalLinkAlt, FaFilter } from "react-icons/fa";

const Portfolio = ({ isDarkMode }) => {
  const categories = [
    "Web Development",
    "Mobile Apps",
    "Desktop Applications",
    "Automation",
    "Twitter Shilling",
    "Discord Shilling",
    "Hire a Moderator",
    "Live Chat Agent",
    "Graphics Design",
    "Motion & Video Production",
  ];

  const [activeFilters, setActiveFilters] = useState([...categories]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const projects = [
    {
      title: "Business Website",
      category: "Web Development",
      image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085",
    },
    {
      title: "Flutter Mobile App",
      category: "Mobile Apps",
      image: "https://images.unsplash.com/photo-1605902711622-cfb43c4437d1",
    },
    {
      title: "Automation Bot",
      category: "Automation",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
    },
    {
      title: "Crypto Twitter Campaign",
      category: "Twitter Shilling",
      image: "https://images.unsplash.com/photo-1611162616475-46b635cb6868",
    },
    {
      title: "Discord Marketing",
      category: "Discord Shilling",
      image: "https://images.unsplash.com/photo-1614680376573-df3480f0c6ff",
    },
    {
      title: "Brand Logo Design",
      category: "Graphics Design",
      image: "https://images.unsplash.com/photo-1626785774573-4b799315345d",
    },
    {
      title: "Motion Graphics Ad",
      category: "Motion & Video Production",
      image: "https://images.unsplash.com/photo-1581091870622-3c1e7b4f1d39",
    },
  ];

  const toggleFilter = (cat) => {
    if (activeFilters.includes(cat)) {
      setActiveFilters(activeFilters.filter((c) => c !== cat));
    } else {
      setActiveFilters([...activeFilters, cat]);
    }
  };

  const filteredProjects = projects.filter((p) =>
    activeFilters.includes(p.category)
  );

  const textColor = isDarkMode ? "#f1f5f9" : "#111";
  const surface = isDarkMode ? "#111827" : "#ffffff";
  const divider = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const hover = isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";
  const activeBg = "rgba(99,102,241,0.18)";
  const primary = "#6366F1";

  // Responsive logic
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dropdown outside click
  useEffect(() => {
    const handler = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Responsive grid columns
  let gridColumns = "repeat(auto-fit,minmax(260px,1fr))"; // default
  if (windowWidth < 768) gridColumns = "1fr"; // Mobile
  else if (windowWidth >= 768 && windowWidth < 1024) gridColumns = "repeat(2,1fr)"; // Tablet
  else if (windowWidth >= 1024 && windowWidth < 1440) gridColumns = "repeat(3,1fr)"; // Web
  else gridColumns = "repeat(4,1fr)"; // Large Desktop

  return (
    <section
      id="portfolio"
      style={{
        padding: windowWidth < 768 ? "60px 15px" : "100px 20px",
        color: textColor,
      }}
    >
      {/* HEADER + FILTER */}
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto 60px auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ maxWidth: windowWidth < 768 ? "100%" : "720px" }}>
          <h2
            style={{
              fontSize: windowWidth < 768 ? "32px" : "44px",
              fontWeight: "700",
              letterSpacing: "-0.5px",
              marginBottom: "12px",
            }}
          >
            Our Portfolio
          </h2>
          <p
            style={{
              fontSize: windowWidth < 768 ? "14px" : "16px",
              lineHeight: "1.7",
              color: isDarkMode ? "#94a3b8" : "#64748b",
            }}
          >
            Discover some of our recent work across web development,
            automation, marketing, and creative production.
          </p>
        </div>

        {/* FILTER BUTTON */}
        <div ref={dropdownRef} style={{ position: "relative" }}>
  <button
    onClick={() => setOpen(!open)}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "12px 18px",
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      background: surface,
      color: textColor,
      boxShadow: "0 6px 25px rgba(0,0,0,0.12)",
    }}
  >
    <FaFilter />
    Filter
    <span
      style={{
        background: primary,
        color: "white",
        borderRadius: "8px",
        padding: "2px 8px",
        fontSize: "12px",
      }}
    >
      {activeFilters.length}
    </span>
  </button>

  {open && (
    <div
      style={{
        position: "absolute",
        top: "55px",
        // Mobile / Android-এ left-align করুন
        left: windowWidth < 768 ? "0" : "auto",
        right: windowWidth < 768 ? "0" : "0", // Desktop / Web-এ right-align
        width: windowWidth < 768 ? `${windowWidth - 30}px` : "280px", // Mobile-এ screen-fit
        maxHeight: "320px",
        overflowY: "auto",
        borderRadius: "16px",
        background: surface,
        boxShadow: "0 20px 45px rgba(0,0,0,0.25)",
        zIndex: 50,
      }}
    >
      {categories.map((cat, i) => {
        const active = activeFilters.includes(cat);
        return (
          <div
            key={i}
            onClick={() => toggleFilter(cat)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 16px",
              cursor: "pointer",
              fontSize: "14px",
              borderBottom:
                i !== categories.length - 1
                  ? `1px solid ${divider}`
                  : "none",
              background: active ? activeBg : "transparent",
              transition: "0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = active ? activeBg : hover)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = active ? activeBg : "transparent")
            }
          >
            <input
              type="checkbox"
              checked={active}
              readOnly
              style={{
                width: "16px",
                height: "16px",
                accentColor: primary,
              }}
            />
            {cat}
          </div>
        );
      })}
    </div>
  )}
</div>
      </div>

      {/* PROJECT GRID */}
      <div
        style={{
          maxWidth: "1100px",
          margin: "auto",
          display: "grid",
          gridTemplateColumns: gridColumns,
          gap: windowWidth < 768 ? "16px" : "28px",
        }}
      >
        {filteredProjects.map((project, index) => (
          <div
            key={index}
            style={{
              background: surface,
              borderRadius: "18px",
              padding: "12px",
              boxShadow: "0 12px 30px rgba(0,0,0,0.15)",
              transition: "all 0.35s ease",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                borderRadius: "14px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <img
                src={project.image}
                alt={project.title}
                style={{
                  width: "100%",
                  height: windowWidth < 768 ? "150px" : "190px",
                  objectFit: "cover",
                  transition: "transform 0.4s ease",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  padding: "12px",
                  background:
                    "linear-gradient(180deg,transparent,rgba(0,0,0,0.8))",
                  color: "white",
                }}
              >
                <h3 style={{ fontSize: windowWidth < 768 ? "14px" : "16px", marginBottom: "3px" }}>
                  {project.title}
                </h3>
                <p style={{ fontSize: windowWidth < 768 ? "11px" : "12px", opacity: 0.85 }}>
                  {project.category}
                </p>
              </div>
            </div>
            <div
              style={{
                marginTop: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: windowWidth < 768 ? "12px" : "13px",
                color: isDarkMode ? "#cbd5e1" : "#555",
              }}
            >
              View Project
              <FaExternalLinkAlt size={windowWidth < 768 ? 10 : 12} />
            </div>
          </div>
        ))}
      </div>

      {/* SEE MORE BUTTON */}
      <div
        style={{
          marginTop: "60px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <button
          style={{
            padding: "14px 28px",
            borderRadius: "14px",
            border: "none",
            fontSize: windowWidth < 768 ? "14px" : "15px",
            fontWeight: "600",
            cursor: "pointer",
            color: "white",
            background: primary,
            boxShadow: "0 10px 30px rgba(99,102,241,0.35)",
            transition: "all 0.3s ease",
          }}
        >
          See More
        </button>
      </div>
    </section>
  );
};

export default Portfolio;